// B-70 — what "/business/backoffice/panel-cierres" opens on.
//
// A client whose reports cover all its offices at once (a daily process: `getCierreDailyProgress`
// lists documents for it, the test "Nuevo cierre" uses) opens on the panel grouped by date, and a
// date's "Ver detalle" opens the usual list on that one day. Every other client gets the list
// exactly as before. Nothing here names a client.
//
// The open date lives in the URL (`?fecha=YYYY-MM-DD`), so the browser's back button returns to
// the grouped panel and a day's detail can be linked.
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Spinner } from "@mantis-sas/ui";

import { selectAuthState } from "../../../store/selectors/authSelectors";
import { getCierreDailyProgress } from "../../../ApiCalls/mantisBusiness";
import { fechaPorDefecto } from "../../../utils/cierreDiaria";
import { restarDias } from "../../../utils/cierrePanel";
import { getFromStorage, setInStorage } from "../../../utils/storage";
import PanelCierres from "./PanelCierres";
import { PanelCierresPorFecha } from "./PanelCierresPorFecha";
import { useClienteCierres } from "./ClienteCierresContext";

const FECHA = /^\d{4}-\d{2}-\d{2}$/;

// The range the person chose on the grouped panel (`?desde=&hasta=`), or null for its last 7 days.
// It lives in the URL beside `fecha` so opening a day and coming back keeps it. An empty end is an
// open one, as clearing an input leaves it.
const rangoDeParams = (searchParams) => {
  if (!searchParams.has("desde") && !searchParams.has("hasta")) return null;
  const extremo = (clave) => {
    const valor = searchParams.get(clave) || "";
    return FECHA.test(valor) ? valor : "";
  };
  return { desde: extremo("desde"), hasta: extremo("hasta") };
};

// The last answer, per business, remembered in this browser so the next visit draws the right
// screen at once while the check runs again behind it. Per business for `claveDeCliente`'s reason:
// one browser can hold sessions in two cierres tenants.
const claveDeDiarios = (mantisBusinessId) => (mantisBusinessId ? `cierres_diario_${mantisBusinessId}` : null);

function PanelCierresInicio(props) {
  const { clientes, clientCode, listo } = useClienteCierres();
  const [searchParams, setSearchParams] = useSearchParams();
  // clientCode -> whether it has a daily process, starting from what this browser remembered.
  const mantisBusinessId = props.mantisBusiness?._id;
  const [diarioPorCliente, setDiarioPorCliente] = useState(() => getFromStorage(claveDeDiarios(mantisBusinessId)) || {});
  // Asked once per client per visit; the remembered answer only decides what draws meanwhile.
  const [verificados, setVerificados] = useState({});

  const timezone = clientes.find((c) => c?.code === clientCode)?.timezone;
  // Today where the client is (`fechaPorDefecto` is yesterday there).
  const hoy = restarDias(fechaPorDefecto(new Date(), timezone), -1);

  useEffect(() => {
    if (!listo || verificados[clientCode]) return undefined;
    let activo = true;
    (async () => {
      const r = await getCierreDailyProgress(props.token, mantisBusinessId, clientCode, hoy).catch(() => null);
      if (!activo) return;
      setVerificados((actual) => ({ ...actual, [clientCode]: true }));
      // An unreadable answer keeps what this browser remembered, or else the list as it always was:
      // on this page every failure resolves toward showing the closings. It is never remembered.
      if (!r?.success) {
        setDiarioPorCliente((actual) => (clientCode in actual ? actual : { ...actual, [clientCode]: false }));
        return;
      }
      const diario = Object.keys(r.data?.documents || {}).length > 0;
      setDiarioPorCliente((actual) => {
        if (actual[clientCode] === diario) return actual;
        const nuevo = { ...actual, [clientCode]: diario };
        setInStorage(claveDeDiarios(mantisBusinessId), nuevo);
        return nuevo;
      });
    })();
    return () => {
      activo = false;
    };
  }, [listo, clientCode, verificados, props.token, mantisBusinessId, hoy]);

  const esDiario = diarioPorCliente[clientCode];
  if (!listo || esDiario === undefined) {
    return (
      <div className="brinks-scope grid place-items-center py-14">
        <Spinner size="lg" />
      </div>
    );
  }
  if (!esDiario) return <PanelCierres />;

  // Changes only the params named, keeping the rest — the range chosen survives opening a day.
  const cambiarParams = (cambios, opciones) => {
    const nuevos = new URLSearchParams(searchParams);
    Object.entries(cambios).forEach(([clave, valor]) => (valor === null ? nuevos.delete(clave) : nuevos.set(clave, valor)));
    setSearchParams(nuevos, opciones);
  };

  const fecha = searchParams.get("fecha");
  if (fecha && FECHA.test(fecha)) {
    return (
      <PanelCierres
        key={fecha}
        fechaDelDia={fecha}
        onVolver={() => cambiarParams({ fecha: null })}
        onCambiarFecha={(otra) => cambiarParams({ fecha: otra })}
      />
    );
  }
  return (
    <PanelCierresPorFecha
      key={clientCode}
      token={props.token}
      mantisBusinessId={mantisBusinessId}
      clientCode={clientCode}
      hoy={hoy}
      rango={rangoDeParams(searchParams)}
      onCambiarRango={(r) => cambiarParams({ desde: r ? r.desde : null, hasta: r ? r.hasta : null }, { replace: true })}
      onVerDetalle={(f) => cambiarParams({ fecha: f })}
    />
  );
}

export default connect(selectAuthState)(PanelCierresInicio);
